import React from 'react';
import { Calendar, MapPin, DollarSign, Edit2, Trash2 } from 'lucide-react';
import type { TravelPlan } from '../../types/travel';

const formatDate = (date: Date | string) => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

interface TravelPlanCardProps {
  plan: TravelPlan;
  onEdit: () => void;
  onDelete: () => void;
}

export function TravelPlanCard({ plan, onEdit, onDelete }: TravelPlanCardProps) {
  const getStatusColor = (status: TravelPlan['status']) => {
    switch (status) {
      case 'planning': return 'bg-yellow-500/20 text-yellow-500';
      case 'booked': return 'bg-blue-500/20 text-blue-500';
      case 'completed': return 'bg-green-500/20 text-green-500';
      case 'canceled': return 'bg-red-500/20 text-red-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  return (
    <div className="bg-[#141414] rounded-xl p-6 hover:ring-1 hover:ring-[#bbdde5] transition-all">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-medium mb-1">{plan.title}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <MapPin className="w-4 h-4" />
            <span>{plan.location}</span>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(plan.status)}`}>
          {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
        </span>
      </div>

      <div className="space-y-3">
        {plan.description && (
          <p className="text-sm text-gray-400">{plan.description}</p>
        )}

        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>{formatDate(plan.start_date)}</span>
          </div>
          {plan.budget && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <span>{formatDate(plan.startDate)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onEdit}
          className="p-2 hover:bg-black/20 rounded-lg transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            if (confirm('Are you sure you want to delete this travel plan?')) {
              onDelete();
            }
          }}
          className="p-2 hover:bg-black/20 rounded-lg transition-colors text-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}